import { Card, Container, Employee } from "@/types/v2";
import { getBidirectionalTechnicianIds } from "@/lib/checkContainerConfiguration";
import { formatTechnicianNames } from "@/lib/cards";
import {
  TechnicianChoiceDialogSubtitle,
  TechnicianOption,
} from "@/components/Common/TechnicianChoiceDialog";

export type TechnicianChoiceResult =
  | { proceed: true; chosenTechnicianId: string | null | undefined }
  | { proceed: false };

type CardForTechnicianChoice = Partial<Pick<Card, "cardNumber" | "vehicle">>;

function subtitleFor(
  card: CardForTechnicianChoice
): TechnicianChoiceDialogSubtitle {
  const vehicleParts = [
    card.vehicle?.year,
    card.vehicle?.make,
    card.vehicle?.model,
  ].filter(Boolean);

  return {
    roNumber: card.cardNumber ?? undefined,
    vehicleDescription: vehicleParts.length
      ? vehicleParts.join(" ")
      : undefined,
  };
}

/**
 * Decides whether a card move needs to ask the user which technician to sync
 * to Tekmetric, and if so, asks via `chooseTechnician`. Gated entirely on
 * `shouldPromptForTechnician` — unlike the label picker, this must never
 * prompt when the flag is off, since `linkedTechnicianId` has pre-existing,
 * shipped uses (inbound container placement) unrelated to this feature.
 *
 * Prompts whenever the destination container has 1 or more bidirectional-eligible
 * technicians (WEBV3-292: previously this only prompted at 2+, silently
 * auto-assigning the sole technician otherwise) — with 0 bidirectional-eligible
 * technicians there is nothing to write back, so no prompt is shown. `candidateIds`
 * reflects the result of `getBidirectionalTechnicianIds` (WEBV3-302), not every
 * `linkedTechnicianId` — a linked-but-opted-out technician is silently excluded from
 * the picker, exactly as if they were never linked at all from this function's point
 * of view. `chosenTechnicianId` on the returned `{ proceed: true }` result is `string`
 * when the user picked a technician, `null` when they clicked "Don't Assign, just move"
 * (an explicit decline — the move still proceeds, nothing is written to Tekmetric), or
 * `undefined` when no choice was ever offered (flag off, or 0 bidirectional-eligible
 * technicians).
 * @param {Container | undefined} toContainer - The destination container.
 * @param {Employee[] | undefined} employees - The shop's employees, for id -> name display.
 * @param {CardForTechnicianChoice} card - The card's RO number and vehicle fields, for the subtitle.
 * @param {(options: TechnicianOption[], subtitle?: TechnicianChoiceDialogSubtitle) => Promise<string | null>} chooseTechnician - Opens the picker; resolves with the chosen technician id, resolves with `null` on an explicit decline, rejects on cancel.
 * @param {boolean} shouldPromptForTechnician - Whether to prompt at all: both the shop's write-back setting AND the caller's admin/office/superAdmin role must hold.
 * @returns {Promise<TechnicianChoiceResult>} Whether to proceed, and which technician (if any) to sync.
 */
export async function resolveTechnicianChoice(
  toContainer: Container | undefined,
  employees: Employee[] | undefined,
  card: CardForTechnicianChoice,
  chooseTechnician: (
    options: TechnicianOption[],
    subtitle?: TechnicianChoiceDialogSubtitle
  ) => Promise<string | null>,
  shouldPromptForTechnician: boolean
): Promise<TechnicianChoiceResult> {
  if (!shouldPromptForTechnician) {
    return { proceed: true, chosenTechnicianId: undefined };
  }

  const candidateIds = getBidirectionalTechnicianIds(toContainer);
  if (candidateIds.length === 0) {
    return { proceed: true, chosenTechnicianId: undefined };
  }

  const options: TechnicianOption[] = candidateIds.map((id) => ({
    id,
    name: formatTechnicianNames(id, employees ?? []),
  }));

  try {
    const chosen = await chooseTechnician(options, subtitleFor(card));
    return { proceed: true, chosenTechnicianId: chosen };
  } catch {
    return { proceed: false };
  }
}
